import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import dataService from '../services/dataService';
import PostCard from '../components/PostCard';
import CreatePostModal from '../components/CreatePostModal';
import EventRegistrationModal from '../components/EventRegistrationModal';
import TrainingRegistrationModal from '../components/TrainingRegistrationModal';
import MeetingRegistrationModal from '../components/MeetingRegistrationModal';
import API_ENDPOINTS, { ASSETS_URL, getImageUrl } from '../config/apiConfig';
import { PlusIcon, MagnifyingGlassIcon, PlusCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import {
    CalendarIcon,
    UsersIcon,
    PaperAirplaneIcon,
    InboxIcon,
    ClockIcon,
    MapPinIcon,
    LinkIcon,
    CurrencyRupeeIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const Dashboard = () => {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [events, setEvents] = useState([]);
    const [trainings, setTrainings] = useState([]);
    const [chapterMeetings, setChapterMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showAnnouncements, setShowAnnouncements] = useState(false);
    const location = useLocation();

    // Registration Modals
    const [isEventRegModalOpen, setIsEventRegModalOpen] = useState(false);
    const [isTrainingRegModalOpen, setIsTrainingRegModalOpen] = useState(false);
    const [isMeetingRegModalOpen, setIsMeetingRegModalOpen] = useState(false);
    const [selectedEventToRegister, setSelectedEventToRegister] = useState(null);
    const [selectedTrainingToRegister, setSelectedTrainingToRegister] = useState(null);
    const [selectedMeetingToRegister, setSelectedMeetingToRegister] = useState(null);



    // Consolidated global search
    const [globalSearch, setGlobalSearch] = useState('');
    const [sentRevenue, setSentRevenue] = useState(0);
    const [receivedRevenue, setReceivedRevenue] = useState(0);

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    fetchPosts(),
                    fetchTrainings(),
                    fetchEvents(),
                    fetchChapterMeetings(),
                    fetchRevenue()
                ]);
            } catch (error) {
                console.error("Error loading dashboard data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, []);

    const fetchRevenue = async () => {
        try {
            const res = await dataService.getRevenue();
            if (res.data.success) {
                setSentRevenue(res.data.sentRevenue || 0);
                setReceivedRevenue(res.data.receivedRevenue || 0);
            }
        } catch (error) {
            console.error('Failed to fetch revenue:', error);
        }
    };



    const fetchPosts = async () => {
        try {
            const response = await dataService.getPosts();
            setPosts(response.data.data);
        } catch (error) {
            toast.error('Failed to fetch posts');
        }
    };

    const fetchEvents = async () => {
        try {
            const res = await dataService.getEvents();
            // Filter only upcoming events for announcements
            const upcoming = (res.data.data || []).filter(e => new Date(e.event_date) >= new Date(new Date().setHours(0,0,0,0)));
            setEvents(upcoming);
        } catch (error) {
            console.error('Failed to fetch events');
        }
    };

    const fetchChapterMeetings = async () => {
        try {
            const res = await dataService.getChapterMeetings();
            // Filter only upcoming meetings for announcements
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const upcoming = (res.data.data || []).filter(m => new Date(m.meeting_date) >= today);
            setChapterMeetings(upcoming);
        } catch (error) {
            console.error('Failed to fetch chapter meetings');
        }
    };

    const fetchTrainings = async () => {
        try {
            const res = await dataService.getTrainings();
            if (res.data.success) {
                // Filter only upcoming trainings for announcements
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const upcoming = (res.data.data || []).filter(t => new Date(t.training_start_date) >= today);
                setTrainings(upcoming);
            }
        } catch (error) {
            console.error('Failed to fetch trainings:', error);
        }
    };

    const handlePostCreated = (newPost) => {
        fetchPosts();
    };

    const handleDeletePost = async (id) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            try {
                await dataService.deletePost(id);
                setPosts(posts.filter(post => post.id !== id));
                toast.success('Post deleted successfully');
            } catch (error) {
                toast.error('Failed to delete post');
            }
        }
    };

    const handleUpdatePost = (id, updatedPost) => {
        setPosts(posts.map(post => post.id === id ? updatedPost : post));
    };

    const filteredPosts = posts.filter(post => {
        if (!globalSearch) return true;
        const searchLower = globalSearch.toLowerCase();
        const matchesTitle = post.title?.toLowerCase().includes(searchLower);
        const matchesDescription = post.description?.toLowerCase().includes(searchLower);
        const matchesAuthor = post.user_name?.toLowerCase().includes(searchLower);
        return matchesTitle || matchesDescription || matchesAuthor;
    });

    // Handle scroll to announcement from notification
    useEffect(() => {
        if (location.state?.targetSection && !loading) {
            const { targetSection, targetId } = location.state;

            // Ensure announcements are visible
            setShowAnnouncements(true);

            // Wait a brief moment for DOM to update and announcements to be visible
            setTimeout(() => {
                let elementId = '';
                if (targetSection === 'event') elementId = `event-${targetId}`;
                if (targetSection === 'training') elementId = `training-${targetId}`;

                if (elementId) {
                    const element = document.getElementById(elementId);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Optional: Highlight the element temporarily
                        element.classList.add('ring-4', 'ring-purple-200', 'transition-all', 'duration-500');
                        setTimeout(() => element.classList.remove('ring-4', 'ring-purple-200'), 2000);
                    }
                }
            }, 500);
        }
    }, [location.state, loading, showAnnouncements]);

    // Handle scroll to post from notification
    useEffect(() => {
        if (location.state?.expandPostId && !loading && posts.length > 0) {
            const { expandPostId } = location.state;
            const elementId = `post-${expandPostId}`;

            // Wait a brief moment for DOM to update
            setTimeout(() => {
                const element = document.getElementById(elementId);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Highlight the element
                    element.classList.add('ring-4', 'ring-blue-200', 'transition-all', 'duration-500');
                    setTimeout(() => element.classList.remove('ring-4', 'ring-blue-200'), 2000);
                }
            }, 500);
        }
    }, [location.state, loading, posts]);

    const now = new Date();
    now.setHours(0, 0, 0, 0); // compare dates only

    const allAnnouncements = [
        ...chapterMeetings.map(m => {
            const d = new Date(m.meeting_date);
            return {
                _orig: m,
                type: 'C',
                id: `meeting-${m.id}`,
                badge: 'Chapter Meeting',
                badgeColor: 'blue',
                title: m.title,
                description: m.description,
                organizer: m.created_by_name || m.chapter_name || 'Organization',
                date: m.meeting_date,
                startTime: m.start_time?.slice(0, 5),
                endTime: m.end_time?.slice(0, 5),
                mode: m.mode,
                location: m.location || m.meeting_link,
                charges: m.charges, // Fixed from meeting_charges
                paymentLink: m.payment_link,
                isRegistered: m.isRegistered || m.user_rsvp === 'attending',
                imagePath: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&q=80&w=1000', // Default professional meeting image
                dateObject: d,
                isPast: d < now
            };
        }),
        ...events.map(e => {
            const d = new Date(e.event_date);
            return {
                _orig: e,
                type: 'E',
                id: `event-${e.id}`,
                badge: 'Event',
                badgeColor: 'purple',
                title: e.title,
                description: e.description,
                organizer: e.organizer_name || 'Organization',
                date: e.event_date,
                startTime: e.event_time_in?.slice(0, 5),
                endTime: e.event_time_out?.slice(0, 5),
                mode: e.event_mode,
                location: e.location,
                charges: e.event_charges,
                paymentLink: e.payment_link,
                isRegistered: e.is_registered,
                imagePath: e.image_path,
                dateObject: d,
                isPast: d < now
            };
        }),
        ...trainings.map(t => {
            const d = new Date(t.training_start_date);
            return {
                _orig: t,
                type: 'T',
                id: `training-${t.id}`,
                badge: 'Training',
                badgeColor: 'indigo',
                title: t.training_title,
                description: t.training_description,
                organizer: t.trainer_name || 'Organization',
                date: t.training_start_date,
                startTime: t.training_start_time?.slice(0, 5),
                endTime: t.training_end_time?.slice(0, 5),
                mode: t.training_mode,
                location: t.training_location || 'Online',
                charges: t.training_charges,
                paymentLink: t.payment_link,
                isRegistered: t.isRegistered || t.is_registered,
                imagePath: t.image_path,
                dateObject: d,
                isPast: d < now
            };
        })
    ].filter(item => !item.isPast) // strictly only show future/current items per user request
    .sort((a, b) => a.dateObject - b.dateObject); // nearest first

    const activeRole = sessionStorage.getItem('activeRole');
    const isMember = activeRole === 'member' || (user?.role === 'member' && activeRole !== 'chapter_admin');

    return (
        <div className="animate-fade-in">
           
            <div className="flex flex-col lg:flex-row gap-8 w-full max-w-7xl mx-auto px-4 justify-center items-start mt-8">

                {/* Feed Section */}
                <div className="flex-1 max-w-2xl space-y-6">
                    {/* Global Search Bar */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search posts, members, content..."
                                value={globalSearch}
                                onChange={(e) => setGlobalSearch(e.target.value)}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                            {globalSearch && (
                                <button
                                    onClick={() => setGlobalSearch('')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Create Post Box */}
                    <div
                        onClick={() => setIsModalOpen(true)}
                        className="group bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex items-center gap-4 cursor-pointer hover:shadow-md hover:border-primary-100 transition-all duration-200"
                    >
                        <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden border border-slate-200 flex-shrink-0">
                            {user?.profile_image ? (
                                <img
                                    src={getImageUrl(user.profile_image)}
                                    alt={user.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400 font-bold text-lg">
                                    {(user?.name || 'U').charAt(0)}
                                </div>
                            )}
                        </div>
                        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200 group-hover:bg-white group-hover:border-primary-200 transition-colors">
                            <p className="text-slate-400 text-sm font-medium">What's on your mind, {user?.name?.split(' ')[0]}?</p>
                        </div>
                        <div className="p-2 bg-primary-50 rounded-xl text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                            <PlusCircleIcon className="w-6 h-6" />
                        </div>
                    </div>

                    {/* Announcement Toggle Button */}
                    <button
                        onClick={() => setShowAnnouncements(!showAnnouncements)}
                        className="group relative bg-gradient-to-r from-primary-600 to-blue-600 text-white py-3.5 px-6 rounded-xl font-bold text-center text-sm tracking-wide w-full hover:from-primary-700 hover:to-blue-700 transition-all shadow-lg shadow-primary-200 hover:shadow-xl active:scale-98"
                    >
                        <span className="flex items-center justify-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            {showAnnouncements ? 'Hide Announcements' : 'View Announcements'}
                        </span>
                    </button>

                    {/* Posts Feed */}
                    <div className="space-y-6">
                        {loading ? (
                            <div className="text-center py-20">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Unified Announcements Feed */}
                                {showAnnouncements && allAnnouncements.length > 0 && allAnnouncements.map((item) => {
                                    const formatDate = (dateString) => {
                                        if (!dateString) return 'TBA';
                                        return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                                    };

                                    const formatTime = (timeString) => {
                                        if (!timeString) return '';
                                        const [hours, minutes] = timeString.split(':');
                                        const hour = parseInt(hours);
                                        const ampm = hour >= 12 ? 'PM' : 'AM';
                                        const hour12 = hour % 12 || 12;
                                        return `${hour12}:${minutes} ${ampm}`;
                                    };

                                    const handleRegisterClick = () => {
                                        if (item.isRegistered || item.isPast) return;
                                        if (item.type === 'C') {
                                            setSelectedMeetingToRegister(item._orig);
                                            setIsMeetingRegModalOpen(true);
                                        } else if (item.type === 'E') {
                                            setSelectedEventToRegister(item._orig);
                                            setIsEventRegModalOpen(true);
                                        } else if (item.type === 'T') {
                                            setSelectedTrainingToRegister(item._orig);
                                            setIsTrainingRegModalOpen(true);
                                        }
                                    };

                                    const getIcon = () => {
                                        if (item.type === 'C') return <UsersIcon className="w-6 h-6" />;
                                        if (item.type === 'E') return <CalendarIcon className="w-6 h-6" />;
                                        if (item.type === 'T') return <span className="text-lg">🎓</span>;
                                    };

                                    // Color maps for Tailwind (avoid dynamic class generation issues)
                                    const colorMap = {
                                        blue:   { bg: 'bg-blue-600',   bgLight: 'bg-blue-50',   border: 'border-blue-100', text: 'text-blue-600',   hover: 'hover:bg-blue-700',   shadow: 'shadow-blue-200' },
                                        purple: { bg: 'bg-purple-600', bgLight: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-600', hover: 'hover:bg-purple-700', shadow: 'shadow-purple-200' },
                                        indigo: { bg: 'bg-indigo-600', bgLight: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-600', hover: 'hover:bg-indigo-700', shadow: 'shadow-indigo-200' }
                                    };
                                    const c = colorMap[item.badgeColor] || colorMap.blue;

                                    return (
                                        <div key={item.id} id={item.id} className={`bg-white rounded-2xl shadow-sm border ${c.border} overflow-hidden mb-6 relative hover:shadow-md transition-all ${item.isPast ? 'opacity-75' : ''}`}>
                                            {/* Top-right badge: type tag */}
                                            <div className={`absolute top-0 right-0 ${c.bg} text-white text-[10px] uppercase font-black px-4 py-1.5 rounded-bl-2xl z-10 flex items-center gap-1.5`}>
                                                {getIcon()}
                                                {item.badge}
                                            </div>

                                            {/* Past/Upcoming indicator */}
                                            {item.isPast && (
                                                <div className="absolute top-0 left-0 bg-slate-500 text-white text-[10px] uppercase font-black px-3 py-1.5 rounded-br-2xl z-10">
                                                    Completed
                                                </div>
                                            )}

                                            {/* Header with title and organizer */}
                                            <div className="p-6 border-b border-gray-50 flex items-center gap-4">
                                                <div className={`w-12 h-12 rounded-2xl ${c.bgLight} flex items-center justify-center ${c.border} border flex-shrink-0 ${c.text}`}>
                                                    {getIcon()}
                                                </div>
                                                <div className="flex-1 min-w-0 pr-24">
                                                    <h4 className="font-black text-slate-900 text-lg line-clamp-1">{item.title}</h4>
                                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                        <span className={`font-black text-[10px] px-2.5 py-0.5 rounded-full uppercase tracking-wider ${c.bgLight} ${c.text} ${c.border} border`}>
                                                            {item.badge}
                                                        </span>
                                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-tight">By {item.organizer}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Image — only if present */}
                                            {item.imagePath && (
                                                <div className="w-full h-48 overflow-hidden relative group">
                                                    <img
                                                        src={getImageUrl(item.imagePath)}
                                                        alt={item.title}
                                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent flex items-end p-6">
                                                        <div className="flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/30">
                                                            <div className={`w-2 h-2 rounded-full ${item.isPast ? 'bg-slate-400' : 'bg-green-400 animate-pulse'}`}></div>
                                                            <span className="text-white font-black text-xs uppercase tracking-widest">{item.mode || 'In-Person'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Details section */}
                                            <div className="p-6">
                                                {/* Mode badge when no image */}
                                                {!item.imagePath && item.mode && (
                                                    <div className="flex items-center gap-2 mb-4">
                                                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${c.bgLight} ${c.border} border`}>
                                                            <div className={`w-2 h-2 rounded-full ${item.isPast ? 'bg-slate-400' : 'bg-green-400 animate-pulse'}`}></div>
                                                            <span className={`font-black text-xs uppercase tracking-widest ${c.text}`}>{item.mode}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="grid grid-cols-2 gap-4 mb-6">
                                                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                                                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase mb-1">
                                                            <ClockIcon className="w-3 h-3" />
                                                            Timing
                                                        </div>
                                                        <div className="text-xs font-bold text-slate-700">
                                                            {formatDate(item.date)}
                                                            {item.startTime && ` • ${formatTime(item.startTime)}`}
                                                            {item.endTime && ` - ${formatTime(item.endTime)}`}
                                                        </div>
                                                    </div>
                                                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                                                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase mb-1">
                                                            <MapPinIcon className="w-3 h-3" />
                                                            Venue
                                                        </div>
                                                        <div className="text-xs font-bold text-slate-700 truncate">
                                                            {item.mode === 'Virtual' ? 'Online' : (item.location || 'In-Person')}
                                                        </div>
                                                    </div>
                                                </div>

                                                <p className="text-sm text-slate-600 mb-6 line-clamp-3 leading-relaxed font-medium">
                                                    {item.description}
                                                </p>

                                                <div className="flex items-center justify-between mb-6 px-1">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[10px] font-black text-slate-400 uppercase">Investment</span>
                                                        <span className={`text-sm font-black ${c.text} uppercase`}>
                                                            {parseFloat(item.charges) > 0 ? `₹${item.charges}` : 'Complimentary'}
                                                        </span>
                                                    </div>
                                                    {item.paymentLink && (
                                                        <a href={item.paymentLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors">
                                                            <LinkIcon className="w-4 h-4 text-slate-500" />
                                                            <span className="text-xs font-bold text-slate-700">Payment Link</span>
                                                        </a>
                                                    )}
                                                </div>

                                                {/* Register button — only for upcoming items */}
                                                {!item.isPast && (
                                                    <button
                                                        onClick={handleRegisterClick}
                                                        disabled={item.isRegistered}
                                                        className={`w-full py-3.5 rounded-xl text-sm font-black shadow-lg transition-all flex items-center justify-center gap-2 ${item.isRegistered
                                                            ? "bg-green-50 text-green-700 cursor-default border-2 border-green-200"
                                                            : `${c.bg} text-white ${c.hover} ${c.shadow}`
                                                            }`}
                                                    >
                                                        {item.isRegistered ? (
                                                            <>Confirmed <CheckCircleIcon className="w-5 h-5" /></>
                                                        ) : (
                                                            <>Register Now <PlusIcon className="w-5 h-5" /></>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {/* Posts */}
                                {filteredPosts.length === 0 ? (
                                    <div className="text-center py-20 bg-white rounded-xl shadow-sm border border-gray-100">
                                        <p className="text-gray-400 font-medium">
                                            {globalSearch
                                                ? "No posts found matching your search."
                                                : "No posts yet. Be the first to share!"}
                                        </p>
                                    </div>
                                ) : (
                                    filteredPosts.map((post, index) => (
                                        <PostCard
                                            key={post.id}
                                            id={`post-${post.id}`}
                                            post={post}
                                            index={index}
                                            onDelete={handleDeletePost}
                                            onUpdate={fetchPosts}
                                        />
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>


            </div>

            <CreatePostModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onPostCreated={handlePostCreated}
            />

            {/* Event Registration Modal */}
            <EventRegistrationModal
                isOpen={isEventRegModalOpen}
                onClose={() => setIsEventRegModalOpen(false)}
                event={selectedEventToRegister}
                onSuccess={() => {
                    setIsEventRegModalOpen(false);
                    fetchEvents();
                }}
            />

            {/* Training Registration Modal */}
            <TrainingRegistrationModal
                isOpen={isTrainingRegModalOpen}
                onClose={() => setIsTrainingRegModalOpen(false)}
                training={selectedTrainingToRegister}
                onSuccess={() => {
                    setIsTrainingRegModalOpen(false);
                    fetchTrainings();
                }}
            />

            {/* Meeting Registration Modal */}
            <MeetingRegistrationModal
                isOpen={isMeetingRegModalOpen}
                onClose={() => setIsMeetingRegModalOpen(false)}
                meeting={selectedMeetingToRegister}
                type="C"
                onSuccess={() => {
                    setIsMeetingRegModalOpen(false);
                    fetchChapterMeetings();
                }}
            />
        </div >
    );
};

export default Dashboard;
